import { useEffect, useId } from "react";
import { FeedbackDialogView } from "./FeedbackDialog.view";
import { DialogHeaderView } from "./components/DialogHeader.view";
import { FiledView } from "./components/Filed.view";
import { useFeedbackForm } from "./useFeedbackForm";
import { useNativeDialog } from "./useNativeDialog";
import styles from "./FeedbackDialog.module.css";
import type { FeedbackDialogProps } from "./FeedbackDialog.types";

interface Props extends FeedbackDialogProps {
  userEmail: string | null;
  hasRun: boolean;
  logCount: number;
}

/**
 * The report form, in a modal.
 *
 * A native `<dialog>`, opened with `showModal()` - see {@link useNativeDialog}
 * for why that is worth a ref. The browser supplies the focus trap, Escape, the
 * top layer and the backdrop, and each of those hand-rolled is a place for a
 * modal to go quietly wrong for somebody using a keyboard or a screen reader.
 *
 * It also gives the behavior we want on a backdrop click, which is nothing: a
 * native dialog ignores one, and here that is right - a stray click should not
 * discard a paragraph somebody has just written about a bug.
 *
 * The element stays mounted once opened, so the draft survives being closed.
 * Somebody who shuts this to go and re-read the screen they are describing
 * comes back to what they had typed. A report that has been *filed* is the one
 * thing that does not survive - see the effect below.
 */
export function FeedbackDialog(props: Props) {
  const dialogRef = useNativeDialog(props.isOpen);
  const titleId = useId();
  const form = useFeedbackForm({ limits: props.limits, onSubmit: props.onSubmit });

  const { receipt, onClose, onStartAnother } = props;
  const isFiled = receipt !== null;
  const { reset } = form;

  /*
   * A filed report stops being a draft, so the fields go the moment one is.
   * Clearing it on the way out instead would miss the reporter who closes the
   * dialog from the receipt - the element stays mounted, and they would reopen
   * it to find the thing they just sent sitting there looking unsent, which is
   * how the same report gets filed twice.
   *
   * It happens on the receipt rather than on the send, because a send that
   * failed still has everything they typed in it and they will want it back.
   */
  useEffect(() => {
    if (isFiled) reset();
  }, [isFiled, reset]);

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby={titleId}
      // Escape fires `cancel`. Letting it through would close the element
      // without telling the parent, leaving `isOpen` true and the dialog
      // impossible to reopen.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <DialogHeaderView
        title={receipt ? "Feedback sent" : "Report feedback"}
        titleId={titleId}
        disabled={props.isSending}
        onClose={onClose}
      />

      {receipt ? (
        <FiledView receipt={receipt} onClose={onClose} onAnother={onStartAnother} />
      ) : (
        <FeedbackDialogView
          {...props}
          kind={form.kind}
          summary={form.summary}
          detail={form.detail}
          expected={form.expected}
          summaryError={form.summaryError}
          canSubmit={form.canSubmit}
          onKind={form.setKind}
          onSummary={form.setSummary}
          onDetail={form.setDetail}
          onExpected={form.setExpected}
          onSend={form.submit}
        />
      )}
    </dialog>
  );
}
